import React, { useState, useEffect, useCallback } from 'react';
import CertificateVerificationCard from './CertificateVerificationCard';
import adminService from '../services/adminService';
import { useNotification } from '../context/NotificationContext';

function AdminCertificatesTab() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showNotification } = useNotification();

  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching all certificates');
      const response = await adminService.getAllCertificates();
      console.log('All certificates response:', response);
      
      // Check different possible response formats
      let certificatesData = [];
      
      if (response && response.certificates) {
        // Standard format with certificates array
        if (Array.isArray(response.certificates)) {
          certificatesData = response.certificates;
        } else if (typeof response.certificates === 'object') {
          // If certificates is an object but not an array, extract values
          certificatesData = Object.values(response.certificates);
        }
      } else if (response && Array.isArray(response)) {
        // Direct array format
        certificatesData = response;
      } else if (response && response.results && Array.isArray(response.results)) {
        // Cloudflare D1 format
        certificatesData = response.results;
      }
      
      // Final check to ensure we have an array
      if (!Array.isArray(certificatesData)) {
        console.error('Failed to parse certificates as array:', certificatesData);
        certificatesData = [];
      }
      
      if (certificatesData.length === 0) {
        console.log('No certificates found');
      }
      
      setCertificates(certificatesData);
    } catch (err) {
      console.error('Error fetching certificates:', err);
      setError('Failed to load pending certificates. Using fallback data.');
      
      // If API call fails, use fallback data
      const fallbackCertificates = [
        {
          id: 'cert-001',
          donorId: 'user-123',
          donorName: 'Green Fields Restaurant',
          organization: 'Green Fields Restaurant',
          type: 'Food Handler Certificate',
          fileUrl: 'https://images.unsplash.com/photo-1557318041-1ce374d55ebf',
          status: 'pending',
          submittedAt: '2025-05-30T10:15:00Z',
          expiryDate: '2026-05-30T00:00:00Z',
          notes: 'Certificate issued by Kenya Department of Public Health'
        },
        {
          id: 'cert-002',
          donorId: 'user-456',
          donorName: 'Nairobi Food Market',
          organization: 'Nairobi Food Market',
          type: 'Food Safety Certificate',
          fileUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40',
          status: 'pending',
          submittedAt: '2025-06-01T14:20:00Z',
          expiryDate: '2026-06-01T00:00:00Z',
          notes: 'Renewed certificate for our third branch'
        }
      ];
      
      setCertificates(fallbackCertificates);
      showNotification('Using fallback certificate data for demonstration.', 'warning');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);
  
  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  const handleApproveCertificate = useCallback(async (certificateId) => {
    try {
      const response = await adminService.verifyCertificate(certificateId, { isApproved: true });
      
      // Update local state by removing approved certificate
      setCertificates(certificates.filter(cert => cert.id !== certificateId));
      
      showNotification(response.message || 'Certificate approved successfully', 'success');
      console.log(`Certificate ${certificateId} approved successfully`);
    } catch (err) {
      console.error('Error approving certificate:', err);
      // Handle error in UI
    }
  }, [certificates, showNotification]);

  const handleRejectCertificate = useCallback(async (certificateId, reason) => {
    try {
      const response = await adminService.verifyCertificate(certificateId, { 
        isApproved: false,
        notes: reason 
      });
      
      // Update local state by removing rejected certificate
      setCertificates(certificates.filter(cert => cert.id !== certificateId));
      
      showNotification(response.message || 'Certificate rejected successfully', 'success');
      console.log(`Certificate ${certificateId} rejected: ${reason}`);
    } catch (err) {
      console.error('Error rejecting certificate:', err);
      // Handle error in UI
    }
  }, [certificates, showNotification]);

  // Render loading state
  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading certificates...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            All Certificates
            <span className="ml-2 px-2 py-0.5 text-sm font-medium rounded-full bg-gray-100 text-gray-800">
              {certificates.length}
            </span>
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            View all food safety certificates submitted by donors
          </p>
        </div>
        
        <div className="px-4 py-5 sm:p-6">
          {certificates.length === 0 ? (
            <div className="text-center py-10">
              <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No pending certificates</h3>
              <p className="mt-1 text-sm text-gray-500">All certificates have been processed. Great job!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {certificates.map(certificate => (
                <CertificateVerificationCard
                  key={certificate.id}
                  certificate={certificate}
                  onApprove={handleApproveCertificate}
                  onReject={handleRejectCertificate}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminCertificatesTab;
